import { useState } from 'react';
import { Link, useNavigate } from 'react-router';

import { Role } from '../../api';
import {
  AsyncSection,
  Form,
  FormError,
  PageHeader,
  Panel,
  SelectField,
  SubmitButton,
  TextField,
  fieldErrors,
  useNotifications,
} from '../../components';
import { PATHS } from '../../routes/paths';
import { useCompanies, useCreateUser, useGroups } from '../queries';
import {
  EMAIL_TAKEN_FIELD,
  EMAIL_TAKEN_FORM,
  ROLE_MEANINGS,
  ROLE_OPTIONS,
  SCOPE_MEANINGS,
  SCOPE_OPTIONS,
  USERS_COPY,
  isEmailTaken,
  isVyttechRole,
  type HumanRole,
  type UserScope,
} from './staffUsers';

/**
 * Creating a person who signs in.
 *
 * Its own screen, so the list next door answers "who has an account?" without a
 * form open over the answer. Three decisions it carries with it:
 *
 * **The company is chosen, never typed**, and it is not a field on the body at
 * all -- the server takes the company from `X-Company-Id`, which is what makes
 * it structurally impossible to create somebody inside a company the request is
 * not for. A vyttech role belongs to no company and sends no header; a customer
 * role must name one -- or, with the scope set to "Grupo" (#391), a company
 * group instead, which travels in the body as `group_id` with no header at all.
 * The scope is asked here and only here: it cannot be changed afterwards.
 *
 * **Only the four human roles are offered.** `machine_read` and `machine_write`
 * belong to credentials, and the API answers 422 to a person given one. A
 * screen that offered them would be offering a mistake, so `HUMAN_ROLES` is the
 * list and there is no path from this file to the other two.
 *
 * **The form is not hidden from an analyst**, who holds no `user:write`. The
 * server is the authority on what a caller may do, and a screen that guesses at
 * permissions is a screen that eventually guesses wrong in the permissive
 * direction. The refusal renders on the form, through the same components as
 * every other error.
 */
export function NewStaffUserPage() {
  const companies = useCompanies();
  const create = useCreateUser();
  const navigate = useNavigate();
  const { notify } = useNotifications();

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<HumanRole>(Role.CUSTOMER_USER);
  const [formCompanyId, setFormCompanyId] = useState('');
  const [scope, setScope] = useState<UserScope>('company');
  const [formGroupId, setFormGroupId] = useState('');
  const groups = useGroups();

  const errors = fieldErrors(create.error);
  const isCustomerRole = !isVyttechRole(role);
  const needsCompany = isCustomerRole && scope === 'company';
  const needsGroup = isCustomerRole && scope === 'group';
  // A 422 names the field; a 409 does not, and this screen supplies it.
  const emailTaken = isEmailTaken(create.error);
  const emailError = errors['email'] ?? (emailTaken ? EMAIL_TAKEN_FIELD : undefined);

  const submit = () => {
    create.mutate(
      {
        user: {
          full_name: fullName,
          email,
          password,
          role,
          ...(needsGroup && { group_id: formGroupId }),
        },
        companyId: needsCompany ? formCompanyId : null,
      },
      {
        onSuccess: (created) => {
          notify('success', `${created.full_name} agora pode entrar.`);
          void navigate(PATHS.staffUsers);
        },
      },
    );
  };

  return (
    <div className="page">
      <PageHeader
        title={USERS_COPY.addPerson}
        description={USERS_COPY.addPersonDescription}
        actions={
          <Link className="button button--ghost" to={PATHS.staffUsers}>
            {USERS_COPY.back}
          </Link>
        }
      />

      <Panel title={USERS_COPY.addPerson} description={USERS_COPY.addPersonDescription}>
        <Form onSubmit={submit} aria-label={USERS_COPY.addPerson}>
          <FormError error={create.error} {...(emailTaken && { message: EMAIL_TAKEN_FORM })} />

          <TextField
            label={USERS_COPY.fullNameLabel}
            value={fullName}
            onChange={setFullName}
            required
            autoComplete="off"
            {...(errors['full_name'] !== undefined && { error: errors['full_name'] })}
          />

          <TextField
            label={USERS_COPY.emailLabel}
            type="email"
            value={email}
            onChange={setEmail}
            required
            hint={USERS_COPY.emailHint}
            autoComplete="off"
            {...(emailError !== undefined && { error: emailError })}
          />

          <TextField
            label={USERS_COPY.passwordLabel}
            type="password"
            value={password}
            onChange={setPassword}
            required
            hint={USERS_COPY.passwordHint}
            autoComplete="new-password"
            {...(errors['password'] !== undefined && { error: errors['password'] })}
          />

          <SelectField
            label={USERS_COPY.profileLabel}
            value={role}
            onChange={(chosen) => {
              if (chosen === '') {
                return;
              }
              setRole(chosen);
            }}
            options={ROLE_OPTIONS}
            required
            hint={ROLE_MEANINGS[role]}
            {...(errors['role'] !== undefined && { error: errors['role'] })}
          />

          {isCustomerRole && (
            <SelectField
              label="Escopo do cadastro"
              value={scope}
              onChange={(chosen) => {
                if (chosen === '') {
                  return;
                }
                setScope(chosen);
              }}
              options={SCOPE_OPTIONS}
              required
              hint={SCOPE_MEANINGS[scope]}
            />
          )}

          {needsGroup && (
            <AsyncSection
              state={groups}
              loadingLabel="Carregando grupos…"
              errorTitle="Não foi possível carregar os grupos"
              isEmpty={(data) => data.items.length === 0}
              emptyTitle="Nenhum grupo cadastrado"
              emptyDescription="Um cadastro por grupo precisa de um grupo econômico, então é preciso cadastrar um antes."
              emptyAction={
                <Link className="button button--secondary" to={PATHS.staffGroupNew}>
                  Cadastrar grupo
                </Link>
              }
            >
              {(data) => (
                <SelectField
                  label="Grupo econômico"
                  value={formGroupId}
                  onChange={setFormGroupId}
                  options={data.items.map((group) => ({
                    value: group.id,
                    label: group.name,
                  }))}
                  placeholder="Escolha um grupo…"
                  required
                  hint="A pessoa atua pelas empresas que estiverem no grupo, escolhendo uma por vez."
                  {...(errors['group_id'] !== undefined && { error: errors['group_id'] })}
                />
              )}
            </AsyncSection>
          )}

          {needsCompany && (
            <AsyncSection
              state={companies}
              loadingLabel="Carregando empresas…"
              errorTitle="Não foi possível carregar as empresas"
              isEmpty={(data) => data.items.length === 0}
              emptyTitle="Nenhuma empresa cadastrada"
              emptyDescription="Um perfil de cliente pertence a uma empresa, então é preciso cadastrar uma antes."
              emptyAction={
                <Link className="button button--secondary" to={PATHS.staffCompanyNew}>
                  Cadastrar empresa
                </Link>
              }
            >
              {(data) => (
                <SelectField
                  label="Empresa cliente"
                  value={formCompanyId}
                  onChange={setFormCompanyId}
                  options={data.items.map((company) => ({
                    value: company.id,
                    label: company.name,
                  }))}
                  placeholder="Escolha uma empresa…"
                  required
                  hint="A pessoa passa a ver o portal desta empresa e de mais nenhuma."
                />
              )}
            </AsyncSection>
          )}

          {!isCustomerRole && (
            <p className="field__hint">
              Um perfil vyttech não pertence a nenhuma empresa cliente: quem o tem trabalha no
              back-office, atravessando todas.
            </p>
          )}

          <div className="form__actions">
            <SubmitButton pending={create.isPending} pendingLabel={USERS_COPY.adding}>
              {USERS_COPY.addPerson}
            </SubmitButton>
            <Link className="button button--ghost" to={PATHS.staffUsers}>
              {USERS_COPY.cancel}
            </Link>
          </div>
        </Form>
      </Panel>
    </div>
  );
}
